/// <reference path="../EventEmitter.ts" />
/// <reference path="./Fields/BaseField.ts" />
/// <reference path="../Validation/IValidator.ts" />


namespace MACRAIL.Forms {

/**
 * Create and show a modal dialog easily.
 */
export class Form extends EventEmitter {

    private element: JQuery;

    private opts: FormOptions;

    private help: any = {};

    private fields: FormFieldCollection = {};

    private validateTimer?: number;

    constructor (opts: FormOptions) {

        super();

        this.element = opts.form;
        this.opts = opts;

        let self = this;

        this.element.on('fieldchanged confirmalertchange', (e: JQueryEventObject) => {
            this.validate();
        });

        this.element.on('mouseover', /*'[data-helpkey]',*/ function(e: JQueryEventObject) {
            let el = e.target;
            let helpKey = $(el).data('helpkey');

            if (helpKey === undefined) {
                helpKey = $(el).parents('[data-helpkey]').data('helpkey');
                if (helpKey === undefined) {
                    return;
                }
            }

            if (self.help[helpKey]) {
                let helpContent = self.help[helpKey];
                if (typeof helpContent === 'function') {
                    helpContent = helpContent();
                }

                let label = $('label[for="' + helpKey + '"]');

                if (self.opts.helpToast) {
                    self.opts.helpToast.find('.toasthelp-title').html(label.length ? label.text() : helpKey);
                    self.opts.helpToast.find('.toasthelp-content').html(helpContent);
                }
            } else {
                if (self.opts.helpToast) {
                    self.opts.helpToast.find('.toasthelp-title').html('Help');
                    self.opts.helpToast.find('.toasthelp-content').html('Relevant help will show here when available.');
                }
            }
        }).trigger('mouseover');
    }

    public autoConfigure() {
        // let Fields = MACRAIL.Forms.Fields;

        this.element.find('[data-form-type]').each((i, el) => {
            let $el = $(el);
            this.addField($el.attr('name') || $el.data('name'), new Forms.Fields[$el.data('form-type')]($el), {
            });
        });
    }

    public addField(name: string, field: Forms.Fields.BaseField, validators: MapOf<ObjectHash>) {

        this.fields[name] = field;

        for (let validator in validators) {
            if (validators.hasOwnProperty(validator)) {
                this.addValidator(name, validator, validators[validator]);
            }
        }

        let self = this;

        let $input = field.getElement();

        $input.on('change', function(this: HTMLElement, e: JQueryEventObject) {
            let id = this.id || $(e.target).parents('[id]').attr('id');

            field.setDirty(true);
            self.validate();
        });

        // HACK: If it's a text input, let's also validate on keyup.
        // Revalidate the ENTIRE form on every keypress? Nope.
        /*if ($input.attr('type') === 'text') {
            $input.on('keyup', function() {
                field.setDirty(true);
                self.validate();
            });
        }*/

        return field;
    }

    public getField(name: string): Forms.Fields.BaseField {
        return this.fields[name];
    }

    public getFields(): FormFieldCollection {
        return this.fields;
    }

    public removeField(name: string) {
        delete this.fields[name];
    }

    public addValidator(field: string, validatorName: string, opts: any) {
        let validator: MACRAIL.Validation.IValidator =
            new MACRAIL.Validation[`${validatorName}Validator`](opts);

        if (this.fields[field] === undefined) {
            throw `Field '${field}' not found in Form.`;
        }

        this.fields[field].addValidator(validator);
    }

    public enableValidator(field: string, validatorName: string) {
        if (this.fields[field] === undefined) {
            throw `Field '${field}' not found in Form.`;
        }

        this.fields[field].enableValidator(validatorName);
    }

    public disableValidator(field: string, validatorName: string) {
        if (this.fields[field] === undefined) {
            throw `Field '${field}' not found in Form.`;
        }

        this.fields[field].disableValidator(validatorName);
    }

    public replaceValidator(field: string, validatorName: string, opts: any) {
        if (this.fields[field] === undefined) {
            throw `Field '${field}' not found in Form.`;
        }

        this.fields[field].replaceValidator(validatorName, opts);
    }

    public setHelp(help: any) {
        this.help = help;

        if (this.opts.helpToast) {
            this.opts.helpToast.find('.toasthelp-content').html(this.help.sentinelcheck);
        }
    }

    public validate = (force = false) => {
        if (this.validateTimer !== undefined) {
            clearTimeout(this.validateTimer);
            this.validateTimer = undefined;
        }

        this.validateTimer = window.setTimeout(() => { this.validateReal(force); }, 10);
    }

    public getValidationMessages(fieldName: string) {
        let field = this.fields[fieldName];

        if (field === undefined) {
            return [];
        }

        let value = field.getValue();
        let hasWarnings = false;
        let hasErrors = false;

        let allMessages = [];

        for (let validator of field.getValidators()) {
            let messages = validator.validate(value);
            if (messages.length > 0) {
                for (let message of messages) {
                    allMessages.push(message);

                    if (message.status === 'error') {
                        hasErrors = true;
                    } else {
                        hasWarnings = true;
                    }
                }
            }
        }

        return allMessages;
    }

    public validateReal = (force = false) => {
        this.trigger('beforevalidate');

        this.validateTimer = undefined;

        for (let fieldName in this.fields) {
            if (this.fields.hasOwnProperty(fieldName)) {
                this.fields[fieldName].validate(force);
            }
        }

        if (this.opts.tabbedform) {
            this.opts.tabbedform.updateTabStatuses();
        }

        this.trigger('validated');
    }

    public setStatus(status: StatusClass, message: string) {
        this.element.find('.form-status').prop('class', 'form-status highlight-' + status).text(message);
    }

    public clearStatus() {
        this.element.find('.form-status').text('');
    }

    public hasWarnings(opts: any = {}) {
        return this.hasFieldsWithClass('warning', opts);
    }

    public hasErrors(opts: any = {}) {
        return this.hasFieldsWithClass('error', opts);
    }

    public setNote(field: string, noteText: string) {
        this.fields[field].setNote(noteText);
    }

    public setMessage(field: string, slug: string, state: Validation.ValidationStatus, message: string) {
        this.fields[field].setMessage(slug, state, message);
    }

    public clearMessage(field: string, slug: string) {
        this.fields[field].clearMessage(slug);
    }

    public getValues() {
        let values: MapOf<string> = {};

        for (let fieldName in this.fields) {
            if (this.fields.hasOwnProperty(fieldName)) {
                values[fieldName] = this.fields[fieldName].getValue();
            }
        }

        return values;
    }

    public getFormData()
    {
        let formData = new FormData();

        for (let fieldName in this.fields) {
            if (this.fields.hasOwnProperty(fieldName)) {
                formData.append(fieldName, this.fields[fieldName].getValue());
            }
        }

        return formData;
    }


    public reset() {
        for (let fieldName in this.fields) {
            if (this.fields.hasOwnProperty(fieldName)) {
                this.fields[fieldName].reset();
            }
        }

        this.clearStatus();
    }

    private hasFieldsWithClass(className: string, opts: any = {}) {
        // HACK: Don't use DOM! This should really loop through the fields with a field.hasEror() call or something.

        let selector = '.has-' + className;


        // If in a .tabbedform we need to exclude the tabs themselves.
        if (this.opts.tabbedform) {
            let ignoreSel = '';
            if (opts.ignoreTabs) {
                for (let tab of opts.ignoreTabs) {
                    ignoreSel += ':not([data-id="' + tab + '"])';
                }
            }

            selector = '.tabbedform-tab:not(.tabbedform-tab-hide)' + ignoreSel + ' ' + selector;
        }

        return this.element.find(selector).length > 0;
    }
}

interface FormFieldCollection {
    [index: string]: MACRAIL.Forms.Fields.BaseField;
}

interface FormOptions {
    /**
     * Root element that contain the form.
     */
    form: JQuery;

    /**
     * Element to use to help toasts.
     */
    helpToast?: JQuery;

    /**
     * The TabbedForm object this form belongs to.
     */
    tabbedform?: TabbedForm;
}

}
